import { useEffect, useRef } from 'react';

export interface DebouncedSaveOptions {
  /** Called when the debounced save rejects or throws. */
  onError?: (error: unknown) => void;
}

function runSave(
  saveFn: () => void | Promise<void>,
  onError?: (error: unknown) => void
): void {
  try {
    const result = saveFn();
    if (result && typeof (result as Promise<void>).then === 'function') {
      void (result as Promise<void>).catch((error) => onError?.(error));
    }
  } catch (error) {
    onError?.(error);
  }
}

/** Debounce async save — skips initial mount; flushes pending edits on unmount. */
export function useDebouncedSave(
  saveFn: () => void | Promise<void>,
  deps: unknown[],
  delayMs = 700,
  options?: DebouncedSaveOptions
): void {
  const saveRef = useRef(saveFn);
  const onErrorRef = useRef(options?.onError);
  const mountedRef = useRef(false);
  const pendingRef = useRef(false);
  const timerRef = useRef<number | null>(null);

  saveRef.current = saveFn;
  onErrorRef.current = options?.onError;

  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }

    pendingRef.current = true;
    if (timerRef.current != null) {
      window.clearTimeout(timerRef.current);
    }

    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      pendingRef.current = false;
      runSave(() => saveRef.current(), onErrorRef.current);
    }, delayMs);

    return () => {
      if (timerRef.current != null) {
        window.clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional debounce on deps
  }, deps);

  // Flush in-flight edits when the owning component unmounts (tab switch, navigation).
  useEffect(() => {
    return () => {
      if (pendingRef.current) {
        pendingRef.current = false;
        runSave(() => saveRef.current(), onErrorRef.current);
      }
    };
  }, []);
}
